import { useWorkspaceText } from '../interface-copy';
import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import workspaceFeedbackCopy from '@/i18n/ns/workspace-feedback';
import { overlaysCopy, type OverlaysCopyKey } from '@/i18n/ns/overlays';
import { useState } from 'react';
import { LoadingState } from '../primitives';
import {
  agentsApi,
  type Agent,
  type AgentDiff,
  type AgentRevision,
  type AgentVersion,
  type AgentVersions,
} from './api';
import { describeSchedule } from './schedule';
import { DraftBar } from './draft-bar';
import { when, type Act } from './shared';

// The Versions tab (PR 4 §7): every published version with its diff, Restore,
// and what the draft changes (the old Changes tab's content).

function scheduleText(
  value: AgentRevision['schedule'],
  copy: Record<OverlaysCopyKey, string>,
  locale: string,
) {
  return value ? `${describeSchedule(value, copy, locale)} · ${value.timezone}` : copy.scheduleNone;
}

export function DiffView({ diff }: { diff: AgentDiff }) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const scheduleCopy = useNamespaceCopy(overlaysCopy);
  const locale = useChatInterfaceLocale();
  const rows: { label: string; before: string; after: string }[] = [];
  for (const [key, label] of Object.entries({
    goal: uiText('Goal'),
    expectedOutput: uiText('Expected result'),
    resources: uiText('Knowledge & connections'),
    boundaries: uiText('Boundaries'),
    skillIds: uiText('Pinned Skills'),
  })) {
    const change = diff[key as keyof typeof diff] as {
      before: string | string[];
      after: string | string[];
    } | null;
    if (change)
      rows.push({
        label,
        before: Array.isArray(change.before)
          ? change.before.join(', ') || uiText('None')
          : change.before || '—',
        after: Array.isArray(change.after)
          ? change.after.join(', ') || uiText('None')
          : change.after || '—',
      });
  }
  if (!diff.skillIds && diff.skill)
    rows.push({
      label: uiText('Skill'),
      before: diff.skill.before?.name ?? '—',
      after: diff.skill.after?.name ?? '—',
    });
  if (diff.schedule)
    rows.push({
      label: uiText('Schedule'),
      before: scheduleText(diff.schedule.before, scheduleCopy, locale),
      after: scheduleText(diff.schedule.after, scheduleCopy, locale),
    });
  if (diff.resultMode)
    rows.push({
      label: uiText('Results'),
      before:
        diff.resultMode.before === 'new'
          ? uiText('A new Result each run')
          : uiText('One Result kept up to date'),
      after:
        diff.resultMode.after === 'new'
          ? uiText('A new Result each run')
          : uiText('One Result kept up to date'),
    });
  if (diff.emailOnFinish)
    rows.push({
      label: uiText('Email'),
      before: diff.emailOnFinish.before ? uiText('On') : uiText('Off'),
      after: diff.emailOnFinish.after ? uiText('On') : uiText('Off'),
    });
  if (!rows.length && !diff.instructions)
    return <p className="ws-muted">{uiText('No changes.')}</p>;
  return (
    <div className="ws-agents-diff">
      {rows.map((row) => (
        <p key={row.label}>
          <span>{row.label}:</span> <del>{row.before}</del> <ins>{row.after}</ins>
        </p>
      ))}
      {diff.instructions && (
        <pre aria-label={uiText('Instructions changes')}>
          {diff.instructions.map((line, index) => (
            <span key={index} className={`ws-diff-${line.type}`}>
              {line.type === 'add' ? '+ ' : line.type === 'remove' ? '− ' : '  '}
              {line.text || ' '}
              {'\n'}
            </span>
          ))}
        </pre>
      )}
    </div>
  );
}

/** The canvas loads the versions with the agent (and again after every change it makes). */
export function VersionsTab({
  agent,
  versions,
  busy,
  act,
}: {
  agent: Agent;
  versions: AgentVersions | null;
  busy: boolean;
  act: Act;
}) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const feedback = useNamespaceCopy(workspaceFeedbackCopy);
  const [selected, setSelected] = useState<number | null>(null);
  if (!versions) return <LoadingState label={uiText('Loading versions…')} />;
  const shown: AgentVersion | undefined =
    versions.versions.find((version) => version.revision === selected) ?? versions.versions[0];
  return (
    <>
      {agent.access.edit && versions.draft && (
        <div className="ws-agents-pinned">
          <DraftBar agent={agent} busy={busy} act={act} />
          <p className="ws-agents-section-title">{uiText('What the draft changes')}</p>
          <DiffView diff={versions.draft.diff} />
        </div>
      )}
      {!versions.versions.length ? (
        <p className="ws-muted">
          {uiText('No published version yet. Try the draft, then publish it.')}
        </p>
      ) : (
        <>
          <ol className="ws-agents-versions" aria-label={uiText('Published versions')}>
            {versions.versions.map((version) => (
              <li
                key={version.revision}
                aria-selected={shown?.revision === version.revision}
                className="ws-agents-version"
              >
                <button
                  className="ws-agents-version-main"
                  onClick={() => setSelected(version.revision)}
                >
                  <span className="ws-agents-version-number">v{version.revision}</span>{' '}
                  <span>
                    <strong>{version.note}</strong>{' '}
                    <small>
                      {version.author?.name ?? uiText('Former member')} ·{' '}
                      {when(version.createdAt, uiText.locale)}
                    </small>
                  </span>
                </button>
                {version.live ? (
                  <span className="ws-agents-pill on">{uiText('Live')}</span>
                ) : (
                  agent.access.edit && (
                    <button
                      className="ws-button small"
                      disabled={busy}
                      onClick={() =>
                        void act(
                          () => agentsApi.restore(agent.id, version.revision),
                          formatCopy(feedback.agentRestoredVersion, { version: version.revision }),
                        )
                      }
                    >
                      {uiText('Restore')}
                    </button>
                  )
                )}
              </li>
            ))}
          </ol>
          {shown && (
            <>
              <p className="ws-agents-section-title">
                {uiRich('What changed in version {value1}', { value1: shown.revision })}
              </p>
              <DiffView diff={shown.diff} />
            </>
          )}
        </>
      )}
      <p className="ws-page-note">{uiText('Every publish is a version you can restore.')}</p>
    </>
  );
}
